import { GetServerSideProps } from 'next'
import { useSearchParams } from 'next/navigation'
import { FormEvent, useEffect, useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import ReCAPTCHA from 'react-google-recaptcha'

import edhrecap from '@/assets/edhrecap.png'
import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import Icon from '@/components/Icon/Icon'
import Main from '@/components/Main/Main'
import Panel from '@/components/Panel/Panel'
import SimpleHeader from '@/components/SimpleHeader/SimpleHeader'
import requestService from '@/services/request.service'
import { useAppSelector } from '@/store/hooks'
import { recaptchaKey } from '@/utils/constants'

type Props = {
  initCountry?: string
}

const Newsletter = () => {
  const user = useAppSelector((state) => state.user)
  const searchParams = useSearchParams()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const reCaptchaRef = useRef<ReCAPTCHA>(null)

  useEffect(() => {
    const email = searchParams.get('email')?.replace(/\s/g, '+')
    if (email) setEmail(email)

    const subscribe = searchParams.get('subscribe')
    if (email && subscribe) handleSubscribe(email)
  }, [])

  useEffect(() => {
    if (user.email && !searchParams.get('email')) setEmail(user.email)
  }, [user.email])

  const handleSubscribe = async (email: string) => {
    setError('')
    setIsLoading(true)

    if (reCaptchaRef.current) {
      const token = await reCaptchaRef.current.executeAsync()
      if (!token) {
        setError('Please complete the captcha.')
        return
      }

      setTimeout(() => {
        setSuccess(true)
      }, 1000)

      try {
        const res = await requestService.post('/api/newsletter', { email, token })
        const resp = await res.json()
        if (resp.success) {
          setSuccess(true)
        } else {
          setError(resp.error)
        }
      } catch (error: any) {
        setError(error.message)
      }

      setIsLoading(false)
    } else {
      setError('Captcha not loaded.')
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    event.stopPropagation()

    event.currentTarget.checkValidity()

    handleSubscribe(email)
  }

  return (
    <Main>
      <Container small>
        <ExplicitHead
          description='EDHRECap - EDHREC Newsletter'
          keywords='EDHREC,EDHRECap,newsletter'
          title='Newsletter | EDHREC'
        />
        <img alt='EDHRECap' className='mb-3 rounded' src={edhrecap.src} />
        <Panel>
          <form className='d-grid pb-3' onSubmit={handleSubmit}>
            <SimpleHeader className='pb-1'>
              {success ? 'Thank you for subscribing!' : 'Subscribe to our newsletter'}
            </SimpleHeader>
            <p>
              {success
                ? "You're all set! We'll send you the latest EDHREC content every week."
                : "Don't miss a thing! Get weekly emails with all the best new content, site updates, and more, on EDHREC."}
            </p>
            <Form.Group className='pb-3'>
              <Form.Label htmlFor='email'>Email</Form.Label>
              <Form.Control
                disabled={success}
                id='email'
                onChange={(e) => setEmail(e.target.value)}
                required
                type='email'
                value={email}
              />
            </Form.Group>
            <ReCAPTCHA ref={reCaptchaRef} sitekey={recaptchaKey} size='invisible' />
            {success ? (
              <Button disabled size='lg' variant='success'>
                <Icon icon='check' />
              </Button>
            ) : (
              <Button disabled={isLoading} size='lg' type='submit' variant={success ? 'secondary' : 'primary'}>
                {isLoading ? <Spinner animation='border' size='sm' /> : 'Subscribe'}
              </Button>
            )}
            {error && (
              <Alert className='mt-3' variant='danger'>
                {error}
              </Alert>
            )}
          </form>
        </Panel>
      </Container>
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req }) => {
  const props: Props = {}
  if (req.headers['cloudfront-viewer-country']) props.initCountry = req.headers['cloudfront-viewer-country'] as string
  return { props }
}

export default Newsletter
